import * as React from "react";
import { ReactNode } from "react";
import { Box, Chip, Stack, Typography } from "@mui/material";
import { Slogan, StoredImage, Translatable } from "../../types";

// Shared building blocks for every creator's "Podgląd" tab: a stylistic
// (not pixel-perfect) approximation of the actual pl-front pages, built in
// plain MUI so the admin never needs Next.js/Tailwind - see Banner.tsx and
// SectionTitle.tsx in pl-front for the components these stand in for.
export const LOCALE = "pl";
export const ACCENT = "#C58511";
export const SOFT_BG = "#FAF2EC";

// Reads a translatable {pl: "..."} map field.
export const t = (map: Translatable<string> | undefined): string =>
  (map && map[LOCALE]) || "";

const isAbsoluteUrl = (src: string) => /^(https?:|blob:|data:)/.test(src);

// Resolves an ImageInput field's value to a displayable <img> src. Covers
// three shapes: an existing plain filename string (bannerImage/sectionImage/
// cover), a freshly picked file's {src: blobUrl, ...} (already usable as-is),
// and a saved benefit icon's {src: filename, ...} (see formatBenefitIcon.ts -
// unlike the other image fields, benefits.icon is never a bare string, and
// its .src is a bare filename that still needs the uploads URL prefix).
export const getImageSrc = (value: StoredImage): string | null => {
  const file = Array.isArray(value) ? value[0] : value;
  if (!file) return null;
  if (typeof file === "string") {
    return `${window["location"]["protocol"]}//${window["env"]["apiDomain"]}/uploads/${file}`;
  }
  if (!file.src) return null;
  if (isAbsoluteUrl(file.src)) return file.src;
  return `${window["location"]["protocol"]}//${window["env"]["apiDomain"]}/uploads/${file.src}`;
};

interface SectionTitleLikeProps {
  title: ReactNode;
  article: ReactNode;
}

// Stand-in for pl-front's <SectionTitle>: a small accent bar + eyebrow +
// heading, reused by every section across every page type.
export const SectionTitleLike = ({ title, article }: SectionTitleLikeProps) => (
  <Stack direction="row" spacing={2} alignItems="center">
    <Box
      sx={{
        width: 6,
        alignSelf: "stretch",
        minHeight: 40,
        bgcolor: ACCENT,
        borderRadius: 1,
      }}
    />
    <Box>
      <Typography sx={{ fontSize: 12, fontWeight: 600, letterSpacing: 0.5 }}>
        {title}
      </Typography>
      <Typography sx={{ fontSize: 26, fontWeight: 300, lineHeight: 1.3 }}>
        {article}
      </Typography>
    </Box>
  </Stack>
);

// The banner section of a creator's live-form values: same shape as Banner
// (see types/models.ts) except bannerImage may also be mid-edit
// (UploadedFile) and bannerSlogans may still be a bare array before
// TranslatableInputs wraps it into {pl: [...]} - see PreviewPage.js's note
// on that behavior.
export interface BannerFormValue {
  bannerImage?: StoredImage;
  bannerTitle?: Translatable<string>;
  bannerSubtitle?: Translatable<string>;
  bannerSlogans?: Translatable<Slogan[]> | Slogan[];
}

// Stand-in for pl-front's <Banner>: every page type (service, main, faq)
// opens with the same hero banner + optional slogans strip.
export const BannerPreview = ({ banner }: { banner?: BannerFormValue }) => {
  const imgSrc = getImageSrc(banner?.bannerImage);
  const rawSlogans = banner?.bannerSlogans;
  const slogans: Slogan[] = Array.isArray(rawSlogans)
    ? rawSlogans
    : rawSlogans?.[LOCALE] || [];

  return (
    <>
      {/* Box size follows pl-front's Banner.tsx: content-driven height, capped at 590px. */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          maxHeight: 590,
          borderRadius: 1,
          overflow: "hidden",
          backgroundColor: "#333",
          backgroundImage: imgSrc ? `url(${imgSrc})` : undefined,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            bgcolor: "rgba(0,0,0,0.2)",
          }}
        />
        <Box
          sx={{
            position: "relative",
            color: "#fff",
            px: { xs: "30px", md: "90px" },
            py: { xs: "60px", md: "90px" },
          }}
        >
          <Typography sx={{ fontSize: 12, fontWeight: 600, lineHeight: "21px" }}>
            {t(banner?.bannerTitle)}
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: 30, md: 42 },
              lineHeight: { xs: "50px", md: "63px" },
              fontWeight: 400,
              my: { xs: "10px", md: "20px" },
            }}
          >
            {t(banner?.bannerSubtitle)}
          </Typography>
          <Chip label="Zamów online" sx={{ bgcolor: "#fff", fontWeight: 600 }} />
        </Box>
      </Box>
      {slogans.length > 0 && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 4,
            bgcolor: SOFT_BG,
            p: { xs: 2, md: 4 },
          }}
        >
          {slogans.map((item, index) => (
            <Box
              key={index}
              sx={{ flex: "1 1 200px" }}
              dangerouslySetInnerHTML={{ __html: item?.slogan || "" }}
            />
          ))}
        </Box>
      )}
    </>
  );
};
